import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { saveBlob } from "../../common/utils/downloadBlob";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { exportCompanyBackupAsAdmin } from "../../services/companyBackup.service";
import PageToaster from "../toaster/PageToaster";

interface CompanyOption {
  company_id: number;
  company_name: string;
}

interface CompanyExportAdminModalProps {
  open: boolean;
  onClose: () => void;
}

const boxStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(480px, 95vw)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
};

const CompanyExportAdminModal: React.FC<CompanyExportAdminModalProps> = ({
  open,
  onClose,
}) => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [exporting, setExporting] = useState(false);

  const containerId = "toaster-company-export-admin";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  useEffect(() => {
    if (!open) return;
    setLoadingCompanies(true);
    api
      .get("/company/admin")
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCompanies([]))
      .finally(() => setLoadingCompanies(false));
  }, [open]);

  useEffect(() => {
    if (!open) setSelectedCompany(null);
  }, [open]);

  const handleExport = async () => {
    if (!selectedCompany) return;
    setExporting(true);
    try {
      const blob = await exportCompanyBackupAsAdmin(selectedCompany.company_id);
      saveBlob(
        blob,
        `company_${selectedCompany.company_id}_backup_${Date.now()}.json`
      );
      autoSuccess(`Backup de "${selectedCompany.company_name}" gerado com sucesso.`);
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao gerar o backup da empresa.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <Typography variant="h6" component="h2">
          Exportar dados de uma empresa
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gera o mesmo backup JSON que o manager/owner geraria, escolhendo a
          empresa alvo.
        </Typography>

        <Autocomplete
          options={companies}
          getOptionLabel={(opt) => opt.company_name || ""}
          value={selectedCompany}
          isOptionEqualToValue={(option, value) => option?.company_id === value?.company_id}
          onChange={(_, v) => setSelectedCompany(v)}
          disabled={exporting}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Empresa"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCompanies && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} disabled={exporting}>
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={!selectedCompany || exporting}
          >
            {exporting ? "Gerando..." : "Baixar backup"}
          </Button>
        </Box>

        <PageToaster containerId={containerId} />
      </Box>
    </Modal>
  );
};

export default CompanyExportAdminModal;
